import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Badge, Collapse } from 'react-bootstrap';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient, API_HOST_BASE } from '../utils/apiUtils';
import './ReportModule.css';

const EvaluationReportForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [participantData, setParticipantData] = useState({
    participant_type: '',
    male_batstateu_participants: '',
    female_batstateu_participants: '',
    male_other_participants: '',
    female_other_participants: '',
    narrative_of_activity: '',
    // Activity rating fields
    activity_excellent_batstateu: '',
    activity_excellent_other: '',
    activity_very_satisfactory_batstateu: '',
    activity_very_satisfactory_other: '',
    activity_satisfactory_batstateu: '',
    activity_satisfactory_other: '',
    activity_fair_batstateu: '',
    activity_fair_other: '',
    activity_poor_batstateu: '',
    activity_poor_other: '',
    // Timeliness rating fields
    timeliness_excellent_batstateu: '',
    timeliness_excellent_other: '',
    timeliness_very_satisfactory_batstateu: '',
    timeliness_very_satisfactory_other: '',
    timeliness_satisfactory_batstateu: '',
    timeliness_satisfactory_other: '',
    timeliness_fair_batstateu: '',
    timeliness_fair_other: '',
    timeliness_poor_batstateu: '',
    timeliness_poor_other: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [existingReport, setExistingReport] = useState(null);
  const [reportStatus, setReportStatus] = useState('');
  // Photo rows structure: [{ row_id, frameCount: 1|2, photos: [...], caption: '' }, ...]
  const [photoRows, setPhotoRows] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [photoCaptions, setPhotoCaptions] = useState([]);
  
  // Legacy photos array for backward compatibility during migration
  const [photos, setPhotos] = useState([]);
  const [showProjectInfo, setShowProjectInfo] = useState(false);

  // Check if we're in edit mode
  const isEditMode = !!id;

  // Check permissions
  const canViewReports = user && ['Extension Coordinator','Extension Head','GAD','Vice Chancellor for Administration and Finance','Vice Chancellor for Research, Development and Extension Services','Chancellor','Admin'].includes(user.role);

  useEffect(() => {
    if (!canViewReports) {
      navigate('/reports');
      return;
    }

    if (isEditMode) {
      // Load existing report data for editing
      fetchExistingReport();
    } else {
      // Reset all form data when creating new report
      setSelectedProject(null);
      setExistingReport(null);
      setPhotos([]);
      setSelectedFiles([]);
      setPhotoCaptions([]);
      setError('');
      setSuccess('');
      setParticipantData({
        participant_type: '',
        male_batstateu_participants: '',
        female_batstateu_participants: '',
        male_other_participants: '',
        female_other_participants: '',
        narrative_of_activity: '',
        // Activity rating fields
        activity_excellent_batstateu: '',
        activity_excellent_other: '',
        activity_very_satisfactory_batstateu: '',
        activity_very_satisfactory_other: '',
        activity_satisfactory_batstateu: '',
        activity_satisfactory_other: '',
        activity_fair_batstateu: '',
        activity_fair_other: '',
        activity_poor_batstateu: '',
        activity_poor_other: '',
        // Timeliness rating fields
        timeliness_excellent_batstateu: '',
        timeliness_excellent_other: '',
        timeliness_very_satisfactory_batstateu: '',
        timeliness_very_satisfactory_other: '',
        timeliness_satisfactory_batstateu: '',
        timeliness_satisfactory_other: '',
        timeliness_fair_batstateu: '',
        timeliness_fair_other: '',
        timeliness_poor_batstateu: '',
        timeliness_poor_other: ''
      });
    }

    fetchProjects();
  }, [canViewReports, navigate, isEditMode, id]);

  // Add a refresh effect when the component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchProjects();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get('/reports/projects');
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingReport = async () => {
    try {
      setLoading(true);
      const { data: report } = await apiClient.get(`/reports/${id}`);
      setExistingReport(report);
      
      // Load the associated project
      const { data: project } = await apiClient.get(`/projects/${report.project_id}`);
      setSelectedProject(project);
      
      // Populate form data with existing report data
      setParticipantData({
        participant_type: report.participant_type || '',
        male_batstateu_participants: report.male_batstateu_participants || '',
        female_batstateu_participants: report.female_batstateu_participants || '',
        male_other_participants: report.male_other_participants || '',
        female_other_participants: report.female_other_participants || '',
        narrative_of_activity: report.narrative_of_activity || '',
        // Activity rating fields
        activity_excellent_batstateu: report.activity_excellent_batstateu || '',
        activity_excellent_other: report.activity_excellent_other || '',
        activity_very_satisfactory_batstateu: report.activity_very_satisfactory_batstateu || '',
        activity_very_satisfactory_other: report.activity_very_satisfactory_other || '',
        activity_satisfactory_batstateu: report.activity_satisfactory_batstateu || '',
        activity_satisfactory_other: report.activity_satisfactory_other || '',
        activity_fair_batstateu: report.activity_fair_batstateu || '',
        activity_fair_other: report.activity_fair_other || '',
        activity_poor_batstateu: report.activity_poor_batstateu || '',
        activity_poor_other: report.activity_poor_other || '',
        // Timeliness rating fields
        timeliness_excellent_batstateu: report.timeliness_excellent_batstateu || '',
        timeliness_excellent_other: report.timeliness_excellent_other || '',
        timeliness_very_satisfactory_batstateu: report.timeliness_very_satisfactory_batstateu || '',
        timeliness_very_satisfactory_other: report.timeliness_very_satisfactory_other || '',
        timeliness_satisfactory_batstateu: report.timeliness_satisfactory_batstateu || '',
        timeliness_satisfactory_other: report.timeliness_satisfactory_other || '',
        timeliness_fair_batstateu: report.timeliness_fair_batstateu || '',
        timeliness_fair_other: report.timeliness_fair_other || '',
        timeliness_poor_batstateu: report.timeliness_poor_batstateu || '',
        timeliness_poor_other: report.timeliness_poor_other || ''
      });
      
      // Load existing photos - convert to row-based structure
      if (report.photos) {
        try {
          const photosData = typeof report.photos === 'string' ? JSON.parse(report.photos) : report.photos;
          setPhotos(photosData);
          
          // Convert to row-based structure if it's a flat array
          if (Array.isArray(photosData) && photosData.length > 0) {
            // Check if it's already row-based (has row_id or frameCount)
            const isRowBased = photosData[0].row_id !== undefined || photosData[0].frameCount !== undefined;
            
            if (!isRowBased) {
              // Convert flat array to row-based structure
              // Group photos into rows of 1-2 photos, preserving existing captions
              const rows = [];
              for (let i = 0; i < photosData.length; i += 2) {
                const rowPhotos = photosData.slice(i, i + 2);
                const hasPhotoCaptions = rowPhotos.some(p => p.caption);
                rows.push({
                  row_id: `row_${Date.now()}_${i}`,
                  frameCount: rowPhotos.length,
                  photos: rowPhotos,
                  caption: hasPhotoCaptions ? '' : (rowPhotos[0]?.caption || ''),
                  captionType: hasPhotoCaptions ? 'photo' : 'row',
                  photoCaptions: hasPhotoCaptions ? rowPhotos.map(p => p.caption || '') : []
                });
              }
              setPhotoRows(rows);
            } else {
              // Already row-based - ensure captionType is set
              const rowsWithDefaults = photosData.map(row => ({
                ...row,
                captionType: row.captionType || 'row',
                photoCaptions: row.photoCaptions || []
              }));
              setPhotoRows(rowsWithDefaults);
            }
          } else {
            setPhotoRows([]);
          }
        } catch (e) {
          console.error('Error parsing photos:', e);
          setPhotos([]);
          setPhotoRows([]);
        }
      } else {
        setPhotoRows([]);
      }
      
    } catch (err) {
      console.error('Failed to load existing report', err);
      setError('Failed to load existing report');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = async (projectId) => {
    // Always reset form data first when project changes
    console.log('handleProjectChange - Resetting existingReport to null');
    setSelectedProject(null);
    setExistingReport(null);
    setPhotos([]);
    setPhotoRows([]);
    setSelectedFiles([]);
    setPhotoCaptions([]);
    setError('');
    setSuccess('');
    
    // Reset participant data to empty state
    setParticipantData({
      participant_type: '',
      male_batstateu_participants: '',
      female_batstateu_participants: '',
      male_other_participants: '',
      female_other_participants: '',
      narrative_of_activity: '',
      // Activity rating fields
      activity_excellent_batstateu: '',
      activity_excellent_other: '',
      activity_very_satisfactory_batstateu: '',
      activity_very_satisfactory_other: '',
      activity_satisfactory_batstateu: '',
      activity_satisfactory_other: '',
      activity_fair_batstateu: '',
      activity_fair_other: '',
      activity_poor_batstateu: '',
      activity_poor_other: '',
      // Timeliness rating fields
      timeliness_excellent_batstateu: '',
      timeliness_excellent_other: '',
      timeliness_very_satisfactory_batstateu: '',
      timeliness_very_satisfactory_other: '',
      timeliness_satisfactory_batstateu: '',
      timeliness_satisfactory_other: '',
      timeliness_fair_batstateu: '',
      timeliness_fair_other: '',
      timeliness_poor_batstateu: '',
      timeliness_poor_other: ''
    });
    
    // If no project selected, return early
    if (!projectId) {
      return;
    }
    
    // Find and set the selected project
    const project = projects.find(p => p.project_id == projectId);
    setSelectedProject(project);
    
    // Fetch full project details for display
    try {
      const { data: fullProjectData } = await apiClient.get(`/projects/${projectId}`);
      console.log('Full project data loaded:', fullProjectData);
      setSelectedProject(fullProjectData);
    } catch (err) {
      console.error('Failed to load full project details', err);
      // Keep the basic project data if full details fail to load
    }
    
    // Check if report already exists for this specific project
    try {
      console.log(`About to check for existing report for project ${projectId}`);
      const { data } = await apiClient.get(`/reports?project_id=${projectId}`);
      console.log(`Checking for existing report for project ${projectId}:`, data);
      if (data && data.length > 0) {
        const report = data[0];
        console.log(`Found existing report:`, report);
        console.log(`Setting existingReport to:`, report);
        setExistingReport(report);
        
        // Only load data if this is the same project
        if (report.project_id == projectId) {
          setParticipantData({
            participant_type: report.participant_type || '',
            male_batstateu_participants: report.male_batstateu_participants || '',
            female_batstateu_participants: report.female_batstateu_participants || '',
            male_other_participants: report.male_other_participants || '',
            female_other_participants: report.female_other_participants || '',
            narrative_of_activity: report.narrative_of_activity || '',
            // Activity rating fields
            activity_excellent_batstateu: report.activity_excellent_batstateu || '',
            activity_excellent_other: report.activity_excellent_other || '',
            activity_very_satisfactory_batstateu: report.activity_very_satisfactory_batstateu || '',
            activity_very_satisfactory_other: report.activity_very_satisfactory_other || '',
            activity_satisfactory_batstateu: report.activity_satisfactory_batstateu || '',
            activity_satisfactory_other: report.activity_satisfactory_other || '',
            activity_fair_batstateu: report.activity_fair_batstateu || '',
            activity_fair_other: report.activity_fair_other || '',
            activity_poor_batstateu: report.activity_poor_batstateu || '',
            activity_poor_other: report.activity_poor_other || '',
            // Timeliness rating fields
            timeliness_excellent_batstateu: report.timeliness_excellent_batstateu || '',
            timeliness_excellent_other: report.timeliness_excellent_other || '',
            timeliness_very_satisfactory_batstateu: report.timeliness_very_satisfactory_batstateu || '',
            timeliness_very_satisfactory_other: report.timeliness_very_satisfactory_other || '',
            timeliness_satisfactory_batstateu: report.timeliness_satisfactory_batstateu || '',
            timeliness_satisfactory_other: report.timeliness_satisfactory_other || '',
            timeliness_fair_batstateu: report.timeliness_fair_batstateu || '',
            timeliness_fair_other: report.timeliness_fair_other || '',
            timeliness_poor_batstateu: report.timeliness_poor_batstateu || '',
            timeliness_poor_other: report.timeliness_poor_other || ''
          });
          
          // Load photos for this specific report
          const photosData = report.photos || [];
          setPhotos(photosData);
          
          // Convert to row-based structure
          if (Array.isArray(photosData) && photosData.length > 0) {
            const isRowBased = photosData[0].row_id !== undefined || photosData[0].frameCount !== undefined;
            if (!isRowBased) {
              const rows = [];
              for (let i = 0; i < photosData.length; i += 2) {
                const rowPhotos = photosData.slice(i, i + 2);
                const hasPhotoCaptions = rowPhotos.some(p => p.caption);
                rows.push({
                  row_id: `row_${Date.now()}_${i}`,
                  frameCount: rowPhotos.length,
                  photos: rowPhotos,
                  caption: hasPhotoCaptions ? '' : (rowPhotos[0]?.caption || ''),
                  captionType: hasPhotoCaptions ? 'photo' : 'row',
                  photoCaptions: hasPhotoCaptions ? rowPhotos.map(p => p.caption || '') : []
                });
              }
              setPhotoRows(rows);
            } else {
              setPhotoRows(photosData);
            }
          } else {
            setPhotoRows([]);
          }
        }
      } else {
        // No existing report for this project
        setExistingReport(null);
        setPhotos([]);
        setPhotoRows([]);
      }
    } catch (err) {
      console.error('Error checking existing report:', err);
      setExistingReport(null);
      setPhotos([]);
    }
  };

  const handleParticipantDataChange = (field, value) => {
    setParticipantData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    setPhotoCaptions(new Array(files.length).fill(''));
  };

  const handleCaptionChange = (index, caption) => {
    const newCaptions = [...photoCaptions];
    newCaptions[index] = caption;
    setPhotoCaptions(newCaptions);
  };

  const handlePhotoUpload = async () => {
    if (!existingReport || selectedFiles.length === 0) {
      setError('Please select photos to upload');
      return;
    }

    setUploadingPhotos(true);
    setError('');
    setSuccess('');

    try {
      console.log('Preparing to upload photos:', selectedFiles.length);
      console.log('Selected files:', selectedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })));
      
      const formData = new FormData();
      selectedFiles.forEach((file, index) => {
        console.log(`Appending file ${index + 1}:`, file.name, 'Type:', file instanceof File ? 'File object' : typeof file);
        formData.append('photos', file);
      });
      formData.append('captions', JSON.stringify(photoCaptions));
      
      // Log FormData contents (for debugging)
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log('  Key:', pair[0], 'Value type:', pair[1] instanceof File ? 'File' : typeof pair[1]);
      }

      // apiClient interceptor will automatically remove Content-Type for FormData
      const response = await apiClient.post(
        `/reports/${existingReport.report_id}/photos`,
        formData
      );

      setSuccess(`${response.data.photos.length} photo(s) uploaded successfully!`);
      setSelectedFiles([]);
      setPhotoCaptions([]);
      
      // Refresh report data to get updated photos
      await fetchExistingReport();
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to upload photos. Please try again.'
      );
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!existingReport) return;

    try {
      await apiClient.delete(`/reports/${existingReport.report_id}/photos/${photoId}`);
      setSuccess('Photo deleted successfully!');
      
      // Remove photo from state immediately
      setPhotos(prev => prev.filter(photo => photo.photo_id != photoId));
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete photo. Please try again.');
    }
  };

  const handleUpdateCaption = async (photoId, newCaption) => {
    if (!existingReport) return;

    try {
      await apiClient.put(`/reports/${existingReport.report_id}/photos/${photoId}`, {
        caption: newCaption
      });
      
      // Update local photos state immediately
      setPhotos(prev => prev.map(photo => 
        photo.photo_id == photoId ? { ...photo, caption: newCaption } : photo
      ));
    } catch (err) {
      console.error('Error updating caption:', err);
      setError('Failed to update caption. Please try again.');
    }
  };

  const handleSubmitReport = async () => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        project_id: selectedProject.project_id,
        participant_type: participantData.participant_type,
        male_batstateu_participants: parseInt(participantData.male_batstateu_participants) || 0,
        female_batstateu_participants: parseInt(participantData.female_batstateu_participants) || 0,
        male_other_participants: parseInt(participantData.male_other_participants) || 0,
        female_other_participants: parseInt(participantData.female_other_participants) || 0,
        narrative_of_activity: participantData.narrative_of_activity,
        // Activity rating fields
        activity_excellent_batstateu: parseInt(participantData.activity_excellent_batstateu) || 0,
        activity_excellent_other: parseInt(participantData.activity_excellent_other) || 0,
        activity_very_satisfactory_batstateu: parseInt(participantData.activity_very_satisfactory_batstateu) || 0,
        activity_very_satisfactory_other: parseInt(participantData.activity_very_satisfactory_other) || 0,
        activity_satisfactory_batstateu: parseInt(participantData.activity_satisfactory_batstateu) || 0,
        activity_satisfactory_other: parseInt(participantData.activity_satisfactory_other) || 0,
        activity_fair_batstateu: parseInt(participantData.activity_fair_batstateu) || 0,
        activity_fair_other: parseInt(participantData.activity_fair_other) || 0,
        activity_poor_batstateu: parseInt(participantData.activity_poor_batstateu) || 0,
        activity_poor_other: parseInt(participantData.activity_poor_other) || 0,
        // Timeliness rating fields
        timeliness_excellent_batstateu: parseInt(participantData.timeliness_excellent_batstateu) || 0,
        timeliness_excellent_other: parseInt(participantData.timeliness_excellent_other) || 0,
        timeliness_very_satisfactory_batstateu: parseInt(participantData.timeliness_very_satisfactory_batstateu) || 0,
        timeliness_very_satisfactory_other: parseInt(participantData.timeliness_very_satisfactory_other) || 0,
        timeliness_satisfactory_batstateu: parseInt(participantData.timeliness_satisfactory_batstateu) || 0,
        timeliness_satisfactory_other: parseInt(participantData.timeliness_satisfactory_other) || 0,
        timeliness_fair_batstateu: parseInt(participantData.timeliness_fair_batstateu) || 0,
        timeliness_fair_other: parseInt(participantData.timeliness_fair_other) || 0,
        timeliness_poor_batstateu: parseInt(participantData.timeliness_poor_batstateu) || 0,
        timeliness_poor_other: parseInt(participantData.timeliness_poor_other) || 0
      };

      console.log('EvaluationReportForm - Payload being sent:', payload);

      let response;
      console.log('Submitting report for project:', selectedProject.project_id);
      console.log('Existing report:', existingReport);
      if (existingReport) {
        // Update existing report
        console.log('Updating existing report:', existingReport.report_id);
        response = await apiClient.put(`/reports/${existingReport.report_id}`, payload);
        setSuccess('Evaluation report updated successfully!');
      } else {
        // Create new report
        console.log('Creating new report for project:', selectedProject.project_id);
        response = await apiClient.post('/reports', payload);
        
        // If we have photo rows to upload, upload them now
        if (photoRows.length > 0 && photoRows.some(row => row.selectedFiles && row.selectedFiles.some(f => f))) {
          console.log('Uploading photo rows for new report...');
          setSuccess('Report created successfully! Uploading photos...');
          
          try {
            // Upload photos for each row
            for (const row of photoRows) {
              const filesToUpload = row.selectedFiles?.filter(f => f) || [];
              if (filesToUpload.length === 0) continue;
              
              console.log(`Uploading row ${row.row_id} with ${filesToUpload.length} photo(s)`);
              
              const formData = new FormData();
              filesToUpload.forEach((file) => {
                formData.append('photos', file);
              });
              formData.append('rowData', JSON.stringify({
                row_id: row.row_id,
                frameCount: row.frameCount,
                caption: row.caption || '',
                captionType: row.captionType || 'row',
                photoCaptions: row.photoCaptions || []
              }));
              
              const photoResponse = await apiClient.post(
                `/reports/${response.data.report_id}/photos`,
                formData
              );
              
              console.log(`Row ${row.row_id} photos uploaded successfully:`, photoResponse.data);
            }
            
            setSuccess('Evaluation Report submitted successfully! All photos uploaded.');
            setPhotoRows([]);
            
            // Redirect to evaluations list after a short delay to show success message
            setTimeout(() => {
              navigate('/reports');
            }, 3000);
          } catch (photoErr) {
            console.error('Error uploading photos:', photoErr);
            setError(`Report created but failed to upload photos: ${photoErr.response?.data?.error || photoErr.response?.data?.message || photoErr.message}. You can upload them later by editing the report.`);
            // Still redirect but show error
            setTimeout(() => {
              navigate('/reports');
            }, 4000);
          }
        } else {
          // No photos to upload, show success message and redirect
          setSuccess('Evaluation Report submitted successfully!');
          setTimeout(() => {
            navigate('/reports');
          }, 2000);
        }
      }

      setExistingReport(response.data);
      
      // If updating existing report, refresh photos
      if (existingReport) {
        await fetchExistingReport();
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to submit evaluation report. Please try again.'
      );
      setSubmitting(false);
    } finally {
      if (!selectedFiles.length || !existingReport) {
        // Only set submitting to false if we're not uploading photos
        setSubmitting(false);
      }
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'On-Going':
        return 'primary';
      case 'Pending':
        return 'warning';
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Helper function to parse arrays from JSON strings or comma-separated strings
  const asList = (value) => {
    if (!value) return [];
    try { 
      const arr = JSON.parse(value); 
      return Array.isArray(arr) ? arr : []; 
    } catch { 
      // If JSON parsing fails, treat as comma-separated string
      return value.split(',').map(s => s.trim()).filter(s => s);
    }
  };

  const renderMonitoringEvaluationTable = (monitoringData) => {
    // Try to render rows if provided as monitoring_evaluation_table.rows
    let rows = [];
    try {
      const parsed = typeof monitoringData === 'string' ? JSON.parse(monitoringData) : monitoringData;
      if (parsed && Array.isArray(parsed.rows)) rows = parsed.rows;
    } catch {}
    return (
      <div className="table-responsive">
        <style>
          {`
            .me-view-table {
              table-layout: fixed !important;
              width: 100% !important;
              border-collapse: collapse !important;
            }
            .me-view-table th:last-child,
            .me-view-table td:last-child {
              border-right: 1px solid #dee2e6 !important;
            }
            .me-view-table th:last-child::after,
            .me-view-table td:last-child::after {
              display: none !important;
              content: none !important;
            }
            .me-view-table th:last-child::before,
            .me-view-table td:last-child::before {
              display: none !important;
              content: none !important;
            }
            .me-view-table *::after {
              display: none !important;
              content: none !important;
            }
            .me-view-table *::before {
              display: none !important;
              content: none !important;
            }
            .me-view-table th,
            .me-view-table td {
              position: relative !important;
            }
            .me-view-table th:last-child,
            .me-view-table td:last-child {
              position: relative !important;
            }
            .me-view-table th:last-child::after,
            .me-view-table td:last-child::after {
              position: absolute !important;
              top: 0 !important;
              right: 0 !important;
              width: 0 !important;
              height: 0 !important;
              display: none !important;
              content: none !important;
            }
          `}
        </style>
        <table className="table table-bordered table-sm me-view-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead className="table-primary">
            <tr>
              <th style={{ width: '8%', fontSize: '0.9rem', padding: '12px 8px' }}>Objectives</th>
              <th style={{ width: '12%', fontSize: '0.9rem', padding: '12px 8px' }}>Performance Indicators</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Baseline Data</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Performance Target</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Data Source</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Collection Method</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Frequency of Data Collection</th>
              <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px 8px' }}>Office/Persons Responsible</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={`mev-${idx}`} style={{ verticalAlign: 'top' }}>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.objectives || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.performance_indicators || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.baseline_data || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.performance_target || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.data_source || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.collection_method || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.frequency || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.responsible || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!canViewReports) {
    return null;
  }

  return (
    <div className="bg-light min-vh-100">
      <Container fluid className="py-4">
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">
                  <i className="bi bi-clipboard-data me-2"></i>
                  {isEditMode ? 'Edit Evaluation Report' : 'Create Evaluation Report'}
                </h2>
                <p className="text-muted mb-0">
                  {isEditMode ? 'Edit existing evaluation report' : 'Generate evaluation reports for completed projects'}
                </p>
              </div>
              <Button variant="outline-secondary" as={Link} to="/reports">
                <i className="bi bi-arrow-left me-2"></i>
                Back to Reports
              </Button>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <i className="bi bi-check-circle me-2"></i>
                {success}
              </Alert>
            )}

            {/* Evaluation Report */}
            <Card className="shadow-sm mb-4">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="bi bi-clipboard-data me-2"></i>
                  Evaluation Report
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <div className="mb-2">
                        <Form.Label className="mb-0">Select Project (Approved by Chancellor) for Report</Form.Label>
                      </div>
                      <Form.Select
                        value={selectedProject?.project_id || ''}
                        onChange={(e) => handleProjectChange(e.target.value)}
                        required
                        disabled={loading || isEditMode}
                      >
                        <option value="">
                          {loading ? 'Loading projects...' : 
                           isEditMode ? `Project: ${selectedProject?.title || 'Loading...'}` : 
                           'Choose an approved project...'}
                        </option>
                        {projects.map(project => (
                          <option key={project.project_id} value={project.project_id}>
                            {project.title}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Only projects approved by the Chancellor are available for reporting
                        {projects.length > 0 && (
                          <span className="ms-2">
                            ({projects.length} approved projects available)
                          </span>
                        )}
                      </Form.Text>
                      {!loading && projects.length === 0 && (
                        <Alert variant="warning" className="mt-2">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          No approved projects found.
                          {user && user.role === 'Extension Coordinator'
                            ? ' You only see projects you coordinated that are approved by the Chancellor.'
                            : ' Projects must be marked as "Approved by Chancellor" to appear in this list.'}
                        </Alert>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Evaluation Form Section - Moved to top */}
                {selectedProject && (
                  <Card className="shadow-sm mb-4">
                    <Card.Header>
                      <h5 className="mb-0">
                        <i className="bi bi-pencil-square me-2"></i>
                        Evaluation Report Form
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      {/* Participant Demographics Section - Editable */}
                      <div className="mb-3 p-3 border rounded">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-pencil-square me-2"></i>
                            <span>Section to be filled out for: {selectedProject.title}</span>
                          </div>
                        </div>
                        <strong>Number of Male and Female and Type of Beneficiaries (Type such as OSY, Children, Women, etc.):</strong>
                        <div className="mt-3">
                          <div className="d-flex align-items-center mb-2">
                            <strong>Type of participants:</strong>
                            <Form.Control
                              type="text"
                              className="ms-2"
                              style={{ maxWidth: '300px' }}
                              placeholder="e.g., OSY, Children, Women, etc."
                              value={participantData.participant_type}
                              onChange={(e) => handleParticipantDataChange('participant_type', e.target.value)}
                            />
                          </div>
                          <div className="table-responsive">
                            <table className="table table-bordered table-sm">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: '20%' }}></th>
                                  <th style={{ width: '25%' }}>BatStateU Participants</th>
                                  <th style={{ width: '25%' }}>Participants from other Institutions</th>
                                  <th style={{ width: '15%' }}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="fw-bold">Male</td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      size="sm"
                                      placeholder="0"
                                      value={participantData.male_batstateu_participants}
                                      onChange={(e) => handleParticipantDataChange('male_batstateu_participants', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      size="sm"
                                      placeholder="0"
                                      value={participantData.male_other_participants}
                                      onChange={(e) => handleParticipantDataChange('male_other_participants', e.target.value)}
                                    />
                                  </td>
                                  <td className="fw-bold text-center">
                                    {(parseInt(participantData.male_batstateu_participants) || 0) + (parseInt(participantData.male_other_participants) || 0)}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-bold">Female</td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      size="sm"
                                      placeholder="0"
                                      value={participantData.female_batstateu_participants}
                                      onChange={(e) => handleParticipantDataChange('female_batstateu_participants', e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      size="sm"
                                      placeholder="0"
                                      value={participantData.female_other_participants}
                                      onChange={(e) => handleParticipantDataChange('female_other_participants', e.target.value)}
                                    />
                                  </td>
                                  <td className="fw-bold text-center">
                                    {(parseInt(participantData.female_batstateu_participants) || 0) + (parseInt(participantData.female_other_participants) || 0)}
                                  </td>
                                </tr>
                                <tr className="table-secondary">
                                  <td className="fw-bold">Grand Total</td>
                                  <td className="fw-bold text-center">
                                    {(parseInt(participantData.male_batstateu_participants) || 0) + (parseInt(participantData.female_batstateu_participants) || 0)}
                                  </td>
                                  <td className="fw-bold text-center">
                                    {(parseInt(participantData.male_other_participants) || 0) + (parseInt(participantData.female_other_participants) || 0)}
                                  </td>
                                  <td className="fw-bold text-center">
                                    {(parseInt(participantData.male_batstateu_participants) || 0) + (parseInt(participantData.female_batstateu_participants) || 0) + 
                                     (parseInt(participantData.male_other_participants) || 0) + (parseInt(participantData.female_other_participants) || 0)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Narrative of Activity Section */}
                          <div className="mt-4">
                            <strong>Narrative of the Activity:</strong>
                            <Form.Control
                              as="textarea"
                              rows={6}
                              className="mt-2"
                              placeholder="Please provide a detailed narrative of the activity, including what happened, key outcomes, and any notable observations..."
                              value={participantData.narrative_of_activity}
                              onChange={(e) => handleParticipantDataChange('narrative_of_activity', e.target.value)}
                              style={{ resize: 'vertical' }}
                            />
                          </div>
                          
                          {/* Evaluation Result Section */}
                          <div className="mt-4">
                            <strong>Evaluation Result (if activity is training, technical advice or seminar):</strong>
                            
                            {/* Activity Rating Table */}
                            <div className="mt-3">
                              <strong>Number of beneficiaries/participants who rated the activity as:</strong>
                              <div className="table-responsive mt-2">
                                <table className="table table-bordered table-sm">
                                  <thead className="table-light">
                                    <tr>
                                      <th style={{ width: '30%' }}>Scale</th>
                                      <th style={{ width: '25%' }}>BatStateU Participants</th>
                                      <th style={{ width: '25%' }}>Participants from other Institutions</th>
                                      <th style={{ width: '20%' }}>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="fw-bold">1.1. Excellent</td>
                                      <td>
                                        <Form.Control
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_excellent_batstateu}
                                          onChange={(e) => handleParticipantDataChange('activity_excellent_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_excellent_other}
                                          onChange={(e) => handleParticipantDataChange('activity_excellent_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.activity_excellent_batstateu) || 0) + (parseInt(participantData.activity_excellent_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">1.2. Very Satisfactory</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_very_satisfactory_batstateu}
                                          onChange={(e) => handleParticipantDataChange('activity_very_satisfactory_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_very_satisfactory_other}
                                          onChange={(e) => handleParticipantDataChange('activity_very_satisfactory_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.activity_very_satisfactory_batstateu) || 0) + (parseInt(participantData.activity_very_satisfactory_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">1.3. Satisfactory</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_satisfactory_batstateu}
                                          onChange={(e) => handleParticipantDataChange('activity_satisfactory_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_satisfactory_other}
                                          onChange={(e) => handleParticipantDataChange('activity_satisfactory_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.activity_satisfactory_batstateu) || 0) + (parseInt(participantData.activity_satisfactory_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">1.4. Fair</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_fair_batstateu}
                                          onChange={(e) => handleParticipantDataChange('activity_fair_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_fair_other}
                                          onChange={(e) => handleParticipantDataChange('activity_fair_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.activity_fair_batstateu) || 0) + (parseInt(participantData.activity_fair_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">1.5. Poor</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_poor_batstateu}
                                          onChange={(e) => handleParticipantDataChange('activity_poor_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.activity_poor_other}
                                          onChange={(e) => handleParticipantDataChange('activity_poor_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.activity_poor_batstateu) || 0) + (parseInt(participantData.activity_poor_other) || 0)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            
                            {/* Timeliness Rating Table */}
                            <div className="mt-4">
                              <strong>Number of beneficiaries/participants who rated the timeliness of the activity as:</strong>
                              <div className="table-responsive mt-2">
                                <table className="table table-bordered table-sm">
                                  <thead className="table-light">
                                    <tr>
                                      <th style={{ width: '30%' }}>Scale</th>
                                      <th style={{ width: '25%' }}>BatStateU Participants</th>
                                      <th style={{ width: '25%' }}>Participants from other Institutions</th>
                                      <th style={{ width: '20%' }}>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="fw-bold">2.1. Excellent</td>
                                      <td>
                                        <Form.Control
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_excellent_batstateu}
                                          onChange={(e) => handleParticipantDataChange('timeliness_excellent_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_excellent_other}
                                          onChange={(e) => handleParticipantDataChange('timeliness_excellent_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.timeliness_excellent_batstateu) || 0) + (parseInt(participantData.timeliness_excellent_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">2.2. Very Satisfactory</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_very_satisfactory_batstateu}
                                          onChange={(e) => handleParticipantDataChange('timeliness_very_satisfactory_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_very_satisfactory_other}
                                          onChange={(e) => handleParticipantDataChange('timeliness_very_satisfactory_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.timeliness_very_satisfactory_batstateu) || 0) + (parseInt(participantData.timeliness_very_satisfactory_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">2.3. Satisfactory</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_satisfactory_batstateu}
                                          onChange={(e) => handleParticipantDataChange('timeliness_satisfactory_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_satisfactory_other}
                                          onChange={(e) => handleParticipantDataChange('timeliness_satisfactory_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.timeliness_satisfactory_batstateu) || 0) + (parseInt(participantData.timeliness_satisfactory_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">2.4. Fair</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_fair_batstateu}
                                          onChange={(e) => handleParticipantDataChange('timeliness_fair_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_fair_other}
                                          onChange={(e) => handleParticipantDataChange('timeliness_fair_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.timeliness_fair_batstateu) || 0) + (parseInt(participantData.timeliness_fair_other) || 0)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">2.5. Poor</td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_poor_batstateu}
                                          onChange={(e) => handleParticipantDataChange('timeliness_poor_batstateu', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control 
                                          type="number" 
                                          min="0" 
                                          size="sm" 
                                          placeholder="0" 
                                          value={participantData.timeliness_poor_other}
                                          onChange={(e) => handleParticipantDataChange('timeliness_poor_other', e.target.value)}
                                        />
                                      </td>
                                      <td className="fw-bold text-center">
                                        {(parseInt(participantData.timeliness_poor_batstateu) || 0) + (parseInt(participantData.timeliness_poor_other) || 0)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          
                          {/* Photos Section */}
                          <div className="mt-4">
                            <strong>Photos:</strong>
                            
                            {/* Photo Upload Section - Row-based Framing */}
                            {selectedProject && (
                              <div className="mt-3 p-3 border border-info rounded" style={{ backgroundColor: '#e7f3ff' }}>
                                <div className="d-flex align-items-center mb-3">
                                  <i className="bi bi-camera text-info me-2"></i>
                                  <strong className="text-info">Upload Photos with Framing:</strong>
                                </div>
                                
                                {/* Add New Row Button */}
                                <div className="mb-3">
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => {
                                      const newRow = {
                                        row_id: `row_${Date.now()}_${Math.random()}`,
                                        frameCount: 1,
                                        photos: [],
                                        caption: '',
                                        captionType: 'row', // 'row' or 'photo'
                                        photoCaptions: [],
                                        selectedFiles: [],
                                        tempFiles: []
                                      };
                                      setPhotoRows([...photoRows, newRow]);
                                    }}
                                  >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add Photo Row
                                  </Button>
                                </div>
                                
                                {/* Photo Rows */}
                                {photoRows.map((row, rowIndex) => (
                                  <div key={row.row_id} className="mb-4 p-3 border rounded" style={{ backgroundColor: '#ffffff' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <strong>Row {rowIndex + 1}</strong>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => {
                                          setPhotoRows(photoRows.filter((r, idx) => idx !== rowIndex));
                                        }}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </Button>
                                    </div>
                                    
                                    {/* Frame Count Selection */}
                                    <div className="mb-3">
                                      <Form.Label><small>Number of frames:</small></Form.Label>
                                      <Form.Select
                                        size="sm"
                                        value={row.frameCount}
                                        onChange={(e) => {
                                          const newFrameCount = parseInt(e.target.value);
                                          const updatedRows = [...photoRows];
                                          updatedRows[rowIndex] = {
                                            ...row,
                                            frameCount: newFrameCount,
                                            // Remove excess photos if reducing frame count
                                            photos: row.photos?.slice(0, newFrameCount) || [],
                                            selectedFiles: row.selectedFiles?.slice(0, newFrameCount) || [],
                                            tempFiles: row.tempFiles?.slice(0, newFrameCount) || [],
                                            photoCaptions: row.photoCaptions?.slice(0, newFrameCount) || []
                                          };
                                          setPhotoRows(updatedRows);
                                        }}
                                      >
                                        <option value={1}>1 Photo</option>
                                        <option value={2}>2 Photos</option>
                                      </Form.Select>
                                    </div>
                                    
                                    {/* Caption Type Selection */}
                                    <div className="mb-3">
                                      <Form.Label><small>Caption Type:</small></Form.Label>
                                      <Form.Select
                                        size="sm"
                                        value={row.captionType || 'row'}
                                        onChange={(e) => {
                                          const updatedRows = [...photoRows];
                                          updatedRows[rowIndex] = { 
                                            ...row, 
                                            captionType: e.target.value,
                                            // Clear captions when switching types
                                            caption: e.target.value === 'row' ? row.caption : '',
                                            photoCaptions: e.target.value === 'photo' ? (row.photoCaptions || Array(row.frameCount).fill('')) : []
                                          };
                                          setPhotoRows(updatedRows);
                                        }}
                                      >
                                        <option value="row">Caption per Row</option>
                                        <option value="photo">Caption per Photo</option>
                                      </Form.Select>
                                    </div>
                                    
                                    {/* Photo Frames with Plus Signs */}
                                    <div className="mb-3">
                                      <Row>
                                        {Array.from({ length: row.frameCount }).map((_, photoIndex) => {
                                          const hasPhoto = row.photos?.[photoIndex] || row.tempFiles?.[photoIndex];
                                          const photoUrl = row.tempFiles?.[photoIndex] || (row.photos?.[photoIndex]?.url && row.photos[photoIndex].url.startsWith('http') 
                                            ? row.photos[photoIndex].url 
                                            : row.photos?.[photoIndex] 
                                              ? `${API_HOST_BASE}/uploads/report-photos/${row.photos[photoIndex].filename}` 
                                              : null);
                                          
                                          return (
                                            <Col md={row.frameCount === 1 ? 12 : 6} key={photoIndex} className="mb-3">
                                              <div 
                                                className="position-relative border rounded d-flex align-items-center justify-content-center"
                                                style={{ 
                                                  minHeight: '200px', 
                                                  backgroundColor: '#f8f9fa',
                                                  cursor: 'pointer',
                                                  borderStyle: 'dashed',
                                                  borderWidth: '2px',
                                                  borderColor: '#dee2e6'
                                                }}
                                                onClick={() => {
                                                  if (!hasPhoto) {
                                                    document.getElementById(`photo-input-${row.row_id}-${photoIndex}`)?.click();
                                                  }
                                                }}
                                              >
                                                <input
                                                  type="file"
                                                  id={`photo-input-${row.row_id}-${photoIndex}`}
                                                  accept="image/*"
                                                  style={{ display: 'none' }}
                                                  onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                      const updatedRows = [...photoRows];
                                                      if (!updatedRows[rowIndex].selectedFiles) {
                                                        updatedRows[rowIndex].selectedFiles = [];
                                                      }
                                                      if (!updatedRows[rowIndex].tempFiles) {
                                                        updatedRows[rowIndex].tempFiles = [];
                                                      }
                                                      updatedRows[rowIndex].selectedFiles[photoIndex] = file;
                                                      updatedRows[rowIndex].tempFiles[photoIndex] = URL.createObjectURL(file);
                                                      setPhotoRows(updatedRows);
                                                    }
                                                  }}
                                                />
                                                
                                                {hasPhoto ? (
                                                  <div className="position-relative w-100 h-100" style={{ minHeight: '200px' }}>
                                                    <img
                                                      src={photoUrl}
                                                      alt={`Photo ${photoIndex + 1}`}
                                                      className="w-100 h-100"
                                                      style={{ 
                                                        objectFit: 'cover',
                                                        borderRadius: '4px'
                                                      }}
                                                      onError={(e) => {
                                                        console.error('Photo failed to load:', e.target.src);
                                                        e.target.style.display = 'none';
                                                      }}
                                                    />
                                                    <Button
                                                      variant="danger"
                                                      size="sm"
                                                      className="position-absolute top-0 end-0 m-2"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const updatedRows = [...photoRows];
                                                        updatedRows[rowIndex].selectedFiles = updatedRows[rowIndex].selectedFiles?.filter((_, idx) => idx !== photoIndex) || [];
                                                        updatedRows[rowIndex].tempFiles = updatedRows[rowIndex].tempFiles?.filter((_, idx) => idx !== photoIndex) || [];
                                                        if (updatedRows[rowIndex].photos) {
                                                          updatedRows[rowIndex].photos = updatedRows[rowIndex].photos.filter((_, idx) => idx !== photoIndex);
                                                        }
                                                        if (updatedRows[rowIndex].photoCaptions) {
                                                          updatedRows[rowIndex].photoCaptions = updatedRows[rowIndex].photoCaptions.filter((_, idx) => idx !== photoIndex);
                                                        }
                                                        setPhotoRows(updatedRows);
                                                      }}
                                                    >
                                                      <i className="bi bi-x"></i>
                                                    </Button>
                                                  </div>
                                                ) : (
                                                  <div className="text-center text-muted">
                                                    <i className="bi bi-plus-circle" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                                    <div className="mt-2">
                                                      <small>Click to add photo</small>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                              
                                              {/* Per-Photo Caption */}
                                              {row.captionType === 'photo' && (
                                                <div className="mt-2">
                                                  <Form.Control
                                                    type="text"
                                                    placeholder={`Caption for photo ${photoIndex + 1}...`}
                                                    value={row.photoCaptions?.[photoIndex] || ''}
                                                    onChange={(e) => {
                                                      const updatedRows = [...photoRows];
                                                      if (!updatedRows[rowIndex].photoCaptions) {
                                                        updatedRows[rowIndex].photoCaptions = Array(row.frameCount).fill('');
                                                      }
                                                      updatedRows[rowIndex].photoCaptions[photoIndex] = e.target.value;
                                                      setPhotoRows(updatedRows);
                                                    }}
                                                    size="sm"
                                                  />
                                                </div>
                                              )}
                                            </Col>
                                          );
                                        })}
                                      </Row>
                                    </div>
                                    
                                    {/* Row Caption (if caption type is 'row') */}
                                    {row.captionType === 'row' && (
                                      <div className="mb-3">
                                        <Form.Label><small>Row Caption (optional):</small></Form.Label>
                                        <Form.Control
                                          type="text"
                                          placeholder="Enter caption for this row..."
                                          value={row.caption || ''}
                                          onChange={(e) => {
                                            const updatedRows = [...photoRows];
                                            updatedRows[rowIndex] = { ...row, caption: e.target.value };
                                            setPhotoRows(updatedRows);
                                          }}
                                          size="sm"
                                        />
                                      </div>
                                    )}
                                    
                                    {/* Upload Button for this Row (if existing report) */}
                                    {existingReport && row.selectedFiles?.some(f => f) && (
                                      <Button
                                        variant="info"
                                        size="sm"
                                        onClick={async () => {
                                          const filesToUpload = row.selectedFiles.filter(f => f);
                                          if (filesToUpload.length === 0) {
                                            setError('Please select at least one photo for this row');
                                            return;
                                          }
                                          
                                          setUploadingPhotos(true);
                                          try {
                                            const formData = new FormData();
                                            filesToUpload.forEach((file) => {
                                              formData.append('photos', file);
                                            });
                                            formData.append('rowData', JSON.stringify({
                                              row_id: row.row_id,
                                              frameCount: row.frameCount,
                                              caption: row.caption || '',
                                              captionType: row.captionType || 'row',
                                              photoCaptions: row.photoCaptions || []
                                            }));
                                            
                                            const response = await apiClient.post(
                                              `/reports/${existingReport.report_id}/photos`,
                                              formData
                                            );
                                            
                                            // Update the row with uploaded photos
                                            const updatedRows = [...photoRows];
                                            updatedRows[rowIndex] = {
                                              ...row,
                                              photos: response.data.photos || [],
                                              selectedFiles: [],
                                              tempFiles: []
                                            };
                                            setPhotoRows(updatedRows);
                                            
                                            setSuccess('Photos uploaded successfully!');
                                            
                                            // Refresh report data
                                            await fetchExistingReport();
                                          } catch (err) {
                                            console.error('Error uploading photos:', err);
                                            setError(err.response?.data?.error || 'Failed to upload photos');
                                          } finally {
                                            setUploadingPhotos(false);
                                          }
                                        }}
                                        disabled={uploadingPhotos}
                                      >
                                        {uploadingPhotos ? (
                                          <>
                                            <i className="bi bi-hourglass-split me-2"></i>
                                            Uploading...
                                          </>
                                        ) : (
                                          <>
                                            <i className="bi bi-upload me-2"></i>
                                            Upload Row Photos
                                          </>
                                        )}
                                      </Button>
                                    )}
                                  </div>
                                ))}
                                
                                {photoRows.length === 0 && (
                                  <div className="text-center text-muted py-3">
                                    <small>Click "Add Photo Row" to start adding photos</small>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Display Existing Photos in Rows */}
                            {photoRows.length > 0 && (
                              <div className="mt-3">
                                <strong>Uploaded Photos:</strong>
                                {photoRows.map((row, rowIndex) => (
                                  <div key={row.row_id} className="mt-3 p-3 border rounded">
                                    <div className="row">
                                      {row.photos && row.photos.map((photo, photoIndex) => (
                                        <div key={photo.photo_id || photoIndex} className={`col-md-${row.frameCount === 1 ? '12' : '6'} mb-3`}>
                                          <div className="card">
                                            <img
                                              src={photo.url && photo.url.startsWith('http') ? photo.url : `${API_HOST_BASE}/uploads/report-photos/${photo.filename}`}
                                              className="card-img-top"
                                              alt={`Row ${rowIndex + 1} Photo ${photoIndex + 1}`}
                                              style={{ height: '200px', objectFit: 'cover' }}
                                              onError={(e) => {
                                                console.error('Photo failed to load:', e.target.src);
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                            <div className="card-body p-2">
                                              <div className="d-flex justify-content-between align-items-start mb-2">
                                                <small className="text-muted">
                                                  {photo.uploaded_at ? new Date(photo.uploaded_at).toLocaleDateString() : ''}
                                                </small>
                                                {existingReport && (
                                                  <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={async () => {
                                                      await handleDeletePhoto(photo.photo_id);
                                                      // Remove photo from row
                                                      const updatedRows = [...photoRows];
                                                      updatedRows[rowIndex].photos = updatedRows[rowIndex].photos.filter(p => p.photo_id !== photo.photo_id);
                                                      if (updatedRows[rowIndex].photos.length === 0) {
                                                        updatedRows.splice(rowIndex, 1);
                                                      }
                                                      setPhotoRows(updatedRows);
                                                    }}
                                                    className="p-1"
                                                    style={{ fontSize: '0.75rem' }}
                                                  >
                                                    <i className="bi bi-trash"></i>
                                                  </Button>
                                                )}
                                              </div>
                                              {/* Per-Photo Caption Display/Edit */}
                                              {row.captionType === 'photo' && existingReport && (
                                                <Form.Control
                                                  type="text"
                                                  placeholder={`Caption for photo ${photoIndex + 1}...`}
                                                  value={photo.caption || ''}
                                                  onChange={async (e) => {
                                                    // Update local state
                                                    const updatedRows = [...photoRows];
                                                    updatedRows[rowIndex].photos[photoIndex].caption = e.target.value;
                                                    setPhotoRows(updatedRows);
                                                    
                                                    // Update in database
                                                    try {
                                                      await apiClient.put(`/reports/${existingReport.report_id}/photos/${photo.photo_id}`, {
                                                        caption: e.target.value
                                                      });
                                                    } catch (err) {
                                                      console.error('Error updating photo caption:', err);
                                                    }
                                                  }}
                                                  size="sm"
                                                  className="mt-2"
                                                />
                                              )}
                                              {row.captionType === 'photo' && !existingReport && photo.caption && (
                                                <small className="text-muted fst-italic d-block mt-2">{photo.caption}</small>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    {/* Row Caption Display/Edit */}
                                    {row.captionType === 'row' && (
                                      <>
                                        {row.caption && !existingReport && (
                                          <div className="mt-2 text-center">
                                            <small className="text-muted fst-italic">{row.caption}</small>
                                          </div>
                                        )}
                                        {existingReport && (
                                          <div className="mt-2">
                                            <Form.Control
                                              type="text"
                                              placeholder="Row caption (optional)..."
                                              value={row.caption || ''}
                                              onChange={(e) => {
                                                const updatedRows = [...photoRows];
                                                updatedRows[rowIndex] = { ...row, caption: e.target.value };
                                                setPhotoRows(updatedRows);
                                              }}
                                              onBlur={async () => {
                                                // Save row caption update
                                                try {
                                                  await apiClient.put(`/reports/${existingReport.report_id}/photos/row/${row.row_id}`, {
                                                    caption: row.caption,
                                                    captionType: row.captionType
                                                  });
                                                } catch (err) {
                                                  console.error('Error updating row caption:', err);
                                                }
                                              }}
                                              size="sm"
                                            />
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Fallback: Display old format photos if no rows but photos exist */}
                            {photoRows.length === 0 && photos.length > 0 && (
                              <div className="mt-3">
                                <strong>Uploaded Photos:</strong>
                                <div className="row mt-2">
                                  {photos.map((photo, index) => (
                                    <div key={photo.photo_id} className="col-md-4 col-sm-6 mb-3">
                                      <div className="card">
                                        <img
                                          src={photo.url && photo.url.startsWith('http') ? photo.url : `${API_HOST_BASE}/uploads/report-photos/${photo.filename}`}
                                          className="card-img-top"
                                          alt={photo.caption || 'Report photo'}
                                          style={{ height: '200px', objectFit: 'cover' }}
                                          onError={(e) => {
                                            console.error('Photo failed to load:', e.target.src);
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <div className="card-body p-2">
                                          <div className="d-flex justify-content-between align-items-start mb-2">
                                            <small className="text-muted">
                                              {new Date(photo.uploaded_at).toLocaleDateString()}
                                            </small>
                                            {existingReport && (
                                              <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeletePhoto(photo.photo_id)}
                                                className="p-1"
                                                style={{ fontSize: '0.75rem' }}
                                              >
                                                <i className="bi bi-trash"></i>
                                              </Button>
                                            )}
                                          </div>
                                          {existingReport ? (
                                            <Form.Control
                                              type="text"
                                              placeholder="Add caption..."
                                              value={photo.caption || ''}
                                              onChange={(e) => handleUpdateCaption(photo.photo_id, e.target.value)}
                                              size="sm"
                                            />
                                          ) : (
                                            <small className="text-muted">
                                              {photo.caption || 'No caption'}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {photoRows.length === 0 && photos.length === 0 && existingReport && (
                              <div className="mt-3 text-center text-muted">
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>N/A</div>
                          </div>
                        )}
                      </div>

                      {/* Status Field - Admin Only */}
                      {user && user.role === 'Admin' && existingReport && (
                        <Form.Group className="mb-3">
                          <Form.Label><strong>Report Status:</strong></Form.Label>
                          <Form.Select
                            name="status"
                            value={reportStatus || existingReport.status}
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              setReportStatus(newStatus);
                              try {
                                await axios.put(`/reports/${existingReport.report_id}/status`, {
                                  status: newStatus
                                });
                                setSuccess('Report status updated successfully!');
                                // Refresh the report data
                                const response = await axios.get(`/reports/${existingReport.report_id}`);
                                setExistingReport(response.data);
                              } catch (err) {
                                setError('Failed to update status: ' + (err.response?.data?.error || err.message));
                              }
                            }}
                            style={{ border: '1px solid #888', fontSize: '1em' }}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Approved by VC-RDES">Approved by VC-RDES</option>
                            <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
                            <option value="Approved by Chancellor">Approved by Chancellor</option>
                            <option value="Rejected by Chancellor">Rejected by Chancellor</option>
                          </Form.Select>
                          <small className="text-muted">Only Admin can change report status</small>
                        </Form.Group>
                      )}

                          <div className="mt-3 d-flex justify-content-end gap-2">
                            <Button 
                              variant={existingReport ? "outline-primary" : "primary"}
                              onClick={handleSubmitReport}
                              disabled={submitting}
                            >
                              {submitting ? (
                                <>
                                  <i className="bi bi-hourglass-split me-2"></i>
                                  {existingReport ? 'Updating...' : 'Submitting...'}
                                </>
                              ) : (
                                <>
                                  <i className={`bi ${existingReport ? 'bi-arrow-clockwise' : 'bi-check-circle'} me-2`}></i>
                                  {existingReport ? 'Update Report' : 'Submit Report'}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Project Information Display */}
                {selectedProject && (
                  <Card className="shadow-sm">
                    <Card.Header 
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowProjectInfo(!showProjectInfo)}
                    >
                      <h5 className="mb-0">
                        <i className={`bi bi-chevron-${showProjectInfo ? 'down' : 'right'} me-2`}></i>
                        <i className="bi bi-info-circle me-2"></i>
                        Project Information
                      </h5>
                    </Card.Header>
                    <Collapse in={showProjectInfo}>
                      <Card.Body>
                      <Row className="mb-3">
                        <Col md={6}><strong>Coordinator:</strong> {selectedProject.coordinator_fullname || '—'}</Col>
                        <Col md={3}><strong>Status:</strong> <Badge bg={getStatusVariant(selectedProject.status)}>{selectedProject.status}</Badge></Col>
                        <Col md={3}><strong>Submitted:</strong> {selectedProject.date_submitted ? new Date(selectedProject.date_submitted).toLocaleString() : '—'}</Col>
                      </Row>
                      <Row>
                        <Col md={6}><strong>Request Type:</strong> {selectedProject.request_type || '—'}</Col>
                        <Col md={6}><strong>Event Type:</strong> {selectedProject.event_type || '—'}</Col>
                      </Row>
                      <Row className="mt-2">
                        <Col md={6}><strong>Location:</strong> {selectedProject.location || '—'}</Col>
                        <Col md={6}>
                          <strong>Duration:</strong> 
                          {selectedProject.start_date ? (
                            <div>
                              <div>
                                <strong>Start:</strong> {new Date(selectedProject.start_date).toLocaleDateString()}
                                {selectedProject.start_time && ` at ${selectedProject.start_time}`}
                              </div>
                              {selectedProject.end_date && (
                                <div>
                                  <strong>End:</strong> {new Date(selectedProject.end_date).toLocaleDateString()}
                                  {selectedProject.end_time && ` at ${selectedProject.end_time}`}
                                </div>
                              )}
                            </div>
                          ) : (
                            '—'
                          )}
                        </Col>
                      </Row>

                      {/* Remarks section - only show for approved or rejected projects */}
                      {(selectedProject.status === 'Approved' || selectedProject.status === 'Rejected') && selectedProject.remarks && (
                        <Row className="mt-3">
                          <Col md={12}>
                            <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #dc3545' }}>
                              <div className="d-flex align-items-start">
                                <i className="bi bi-exclamation-triangle-fill text-danger me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                                <div>
                                  <strong className="text-danger">
                                    <i className="bi bi-exclamation-circle-fill me-1"></i>Remarks:
                                  </strong>
                                  <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{selectedProject.remarks}</div>
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}
                      
                      <hr />
                      <Row>
                        <Col md={6}><strong>Extension Agenda:</strong><div>{asList(selectedProject.extension_agenda).join(', ') || '—'}</div></Col>
                        <Col md={6}><strong>SDG Goals:</strong><div>{asList(selectedProject.sdg_goals).join(', ') || '—'}</div></Col>
                      </Row>
                      <hr />
                      
                      {/* Project leadership section */}
                      <div className="mb-3">
                        <strong>Project Leader:</strong>
                        <div className="mt-1">{selectedProject.project_leader_name || '—'}</div>
                      </div>
                      <div className="mb-3">
                        <strong>Assistant Project Leaders:</strong>
                        <div className="mt-1">{asList(selectedProject.assistant_project_leader_names).join(', ') || '—'}</div>
                      </div>
                      <div className="mb-3">
                        <strong>Coordinators:</strong>
                        <div className="mt-1">{asList(selectedProject.coordinator_names).join(', ') || '—'}</div>
                      </div>
                      <hr />
                      {/* Offices and Programs */}
                      {[
                        ['offices_involved','Office/s / College/s / Organization/s Involved'],
                        ['programs_involved','Program/s Involved'],
                        ['partner_agencies','Partner Agencies'],
                        ['beneficiaries','Beneficiaries'],
                        ['total_cost','Total Cost']
                      ].map(([key, label]) => (
                        <div className="mb-3" key={key}>
                          <strong>{label}:</strong>
                          <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{selectedProject[key] || '—'}</div>
                        </div>
                      ))}

                      {/* Fund Source placed after Total Cost (as in proposal) */}
                      <div className="mb-3">
                        <strong>Fund Source:</strong>
                        <div className="mt-1">{asList(selectedProject.fund_source).join(', ') || '—'}</div>
                      </div>

                      {/* Narrative Sections */}
                      {[
                        ['rationale','Rationale'],
                        ['objectives','Objectives (General and Specific)'],
                        ['expected_output','Expected Output'],
                        ['strategies_methods','Description, Strategies and Methods'],
                        ['financial_plan_details','Financial Plan'],
                        ['functional_relationships','Functional Relationships']
                      ].map(([key, label]) => (
                        <div className="mb-3" key={key}>
                          <strong>{label}:</strong>
                          <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{selectedProject[key] || '—'}</div>
                        </div>
                      ))}

                      {/* Monitoring & Evaluation after narrative sections */}
                      <div className="mb-3">
                        <strong>Monitoring and Evaluation Plan:</strong>
                        <div className="mt-2">
                          {renderMonitoringEvaluationTable(selectedProject.monitoring_evaluation)}
                        </div>
                      </div>

                      {/* Sustainability Plan last */}
                      <div className="mb-3">
                        <strong>Sustainability Plan:</strong>
                        <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{selectedProject.sustainability_plan || '—'}</div>
                      </div>
                      </Card.Body>
                    </Collapse>
                  </Card>
                )}


              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EvaluationReportForm;
